import { create } from 'zustand'
import { apiClient } from '../api/client'
import type { User, UserPreferences } from '../types'

// Lazy-load PostHog to reduce bundle size
const identifyUser = (userId: string, traits: Record<string, any>) => {
  import('posthog-js').then(({ default: posthog }) => {
    posthog.identify(userId, traits)
  }).catch(() => {})
}
const resetPostHog = () => {
  import('posthog-js').then(({ default: posthog }) => {
    posthog.reset()
  }).catch(() => {})
}

const TOKEN_KEY = 'apex_token'
const REFRESH_TOKEN_KEY = 'apex_refresh_token'

// Default preferences
const defaultPreferences: UserPreferences = {
  // Notifications
  notifications_enabled: true,
  price_alerts: true,
  score_alerts: true,
  insider_alerts: true,
  earnings_alerts: true,
  geopolitical_alerts: false,
  macro_alerts: false,
  daily_brief_time: '08:00',
  digest_frequency: 'realtime',
  min_severity: 'all',
  email_digest: false,
  email_alerts: '',

  // Display
  default_chart_period: '1M',
  chart_style: 'area',
  score_alert_threshold: 10,
  score_display: 'with_sub',
  watchlist_sort: 'score_desc',
  watchlist_view: 'cards',
  auto_refresh: '1m',

  // Dashboard widgets
  widget_market_pulse: true,
  widget_top_signal: true,
  widget_score_leaders: true,
  widget_daily_brief: true,
  widget_alert_feed: true,
  widget_catalysts: true,
  widget_watchlist: true,

  // Data
  data_refresh_rate: '30s',
  manual_portfolio: true,

  // Onboarding
  onboarding_complete: true,
  investor_profile: [],
  risk_tolerance: 3,
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean

  // Actions
  initAuth: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (fullName: string, email: string, password: string, tier: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<boolean>
  updateUser: (data: Partial<User>) => void
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>
  setLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,

  initAuth: async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

    if (!token) {
      set({ isInitialized: true, isAuthenticated: false })
      return
    }

    set({ token, refreshToken, isLoading: true })

    try {
      // Add 5 second timeout to prevent app hanging on refresh
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Auth timeout')), 5000)
      )
      const authPromise = apiClient.get('/users/me', {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.data)

      const user = await Promise.race([authPromise, timeoutPromise]) as any

      // Merge default preferences with user preferences
      const mergedPrefs = { ...defaultPreferences, ...user.preferences }
      set({
        user: { ...user, preferences: mergedPrefs },
        isAuthenticated: true,
        isInitialized: true,
        isLoading: false
      })
    } catch (error: any) {
      // On timeout or 401, try refresh; on other errors, clear auth
      if (error.message === 'Auth timeout' || error.response?.status === 401) {
        const refreshed = await get().refreshAccessToken()
        if (!refreshed) {
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(REFRESH_TOKEN_KEY)
          set({ user: null, token: null, refreshToken: null, isAuthenticated: false, isInitialized: true, isLoading: false })
        } else {
          set({ isInitialized: true, isLoading: false })
        }
      } else {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        set({ user: null, token: null, refreshToken: null, isAuthenticated: false, isInitialized: true, isLoading: false })
      }
    }
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true })

    // Backend expects form-urlencoded for OAuth2 login
    const formData = new URLSearchParams()
    formData.append('username', email)
    formData.append('password', password)

    try {
      // Step 1: Get tokens from login endpoint (backend returns tokens only, not user)
      const response = await apiClient.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      })

      const { access_token, refresh_token } = response.data

      // Step 2: Store tokens immediately
      localStorage.setItem(TOKEN_KEY, access_token)
      if (refresh_token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token)
      }

      set({ token: access_token, refreshToken: refresh_token || null })

      // Step 3: Fetch user profile using the new token
      const userResponse = await apiClient.get('/users/me', {
        headers: { Authorization: `Bearer ${access_token}` }
      })

      const user = userResponse.data
      // Merge default preferences with user preferences
      const mergedPrefs = { ...defaultPreferences, ...user.preferences }

      // Identify user in PostHog - Phase H analytics (lazy loaded)
      identifyUser(user.id, {
        email: user.email,
        name: user.full_name,
        tier: user.subscription_tier,
      })

      set({
        user: { ...user, preferences: mergedPrefs },
        isAuthenticated: true,
        isLoading: false
      })
    } catch (error: any) {
      set({ isLoading: false })
      throw new Error(error.response?.data?.detail || 'Login failed')
    }
  },

  register: async (fullName: string, email: string, password: string, tier: string) => {
    set({ isLoading: true })

    try {
      // Step 1: Register and get tokens
      const response = await apiClient.post('/auth/register', {
        full_name: fullName,
        email,
        password,
        subscription_tier: tier
      })

      const { access_token, refresh_token } = response.data

      // Step 2: Store tokens immediately
      localStorage.setItem(TOKEN_KEY, access_token)
      if (refresh_token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token)
      }

      set({ token: access_token, refreshToken: refresh_token || null })

      // Step 3: Fetch user profile using the new token
      const userResponse = await apiClient.get('/users/me', {
        headers: { Authorization: `Bearer ${access_token}` }
      })

      const user = userResponse.data
      // New users get default preferences merged
      const mergedPrefs = { ...defaultPreferences, ...user.preferences }

      // Identify user in PostHog - Phase H analytics (lazy loaded)
      identifyUser(user.id, {
        email: user.email,
        name: user.full_name,
        tier: user.subscription_tier,
      })

      set({
        user: { ...user, preferences: mergedPrefs },
        isAuthenticated: true,
        isLoading: false
      })
    } catch (error: any) {
      set({ isLoading: false })
      throw new Error(error.response?.data?.detail || 'Registration failed')
    }
  },

  logout: () => {
    // Reset PostHog on logout - Phase H analytics
    resetPostHog()
    // Clear all auth tokens
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem('apex_user_profile')
    // Note: React Query cache is cleared via queryClient.clear() in the API client's 401 handler
    set({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false
    })
  },

  refreshAccessToken: async () => {
    const refreshToken = get().refreshToken || localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) return false

    // Validate token format before making network call
    try {
      JSON.parse(atob(refreshToken.split('.')[1]))
    } catch {
      // Invalid token format, clear it
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      set({ user: null, token: null, refreshToken: null, isAuthenticated: false })
      return false
    }

    try {
      const response = await apiClient.post('/auth/refresh', {}, {
        headers: { Authorization: `Bearer ${refreshToken}` }
      })

      const { access_token } = response.data
      localStorage.setItem(TOKEN_KEY, access_token)
      set({ token: access_token })

      // Re-fetch user
      const userResponse = await apiClient.get('/users/me', {
        headers: { Authorization: `Bearer ${access_token}` }
      })

      const user = userResponse.data
      const mergedPrefs = { ...defaultPreferences, ...user.preferences }
      set({
        user: { ...user, preferences: mergedPrefs },
        isAuthenticated: true
      })

      return true
    } catch (error: any) {
      // FIX: On 401/404 (user doesn't exist), clear ALL tokens - hard logout
      const status = error?.response?.status
      if (status === 401 || status === 404) {
        console.warn('Refresh token rejected (user may not exist), clearing all auth')
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        // Note: React Query cache is cleared via queryClient.clear() in the API client's 401 handler
        set({ user: null, token: null, refreshToken: null, isAuthenticated: false })
      }
      return false
    }
  },

  updateUser: (userData) => {
    set((state) => ({
      user: state.user ? { ...state.user, ...userData } : null
    }))
  },

  updatePreferences: async (prefs: Partial<UserPreferences>) => {
    const { user, token } = get()
    if (!user || !token) return

    const newPrefs = { ...user.preferences, ...prefs }

    // Optimistic update
    set({
      user: { ...user, preferences: newPrefs }
    })

    // Persist to server
    try {
      await apiClient.patch('/users/me', { preferences: newPrefs }, {
        headers: { Authorization: `Bearer ${token}` }
      })
    } catch {
      // Revert on error
      set({ user })
    }
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  }
}))

export default useAuthStore
